import { useEffect, useRef, useState } from "react";

/**
 * Custom cursor with a trailing ring + a precise dot. Grows when hovering
 * over interactive elements (anchors, buttons, [data-cursor="grow"]).
 *
 * Hidden via CSS on touch devices.
 */
export const CustomCursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const [grow, setGrow] = useState(false);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    let raf = 0;
    let dx = 0, dy = 0, rx = 0, ry = 0;

    const onMove = (e) => {
      setHidden(false);
      dx = e.clientX;
      dy = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${dx - 3}px, ${dy - 3}px, 0)`;
      }
    };
    const onLeave = () => setHidden(true);

    const animate = () => {
      // ring lags slightly behind for a smooth-trail effect
      rx += (dx - rx) * 0.18;
      ry += (dy - ry) * 0.18;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${rx}px, ${ry}px, 0)`;
      }
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    const isInteractive = (target) => {
      if (!target || !target.closest) return false;
      return !!target.closest(
        'a, button, [role="button"], input, select, textarea, [data-cursor="grow"], .cursor-pointer'
      );
    };
    const onOver = (e) => setGrow(isInteractive(e.target));

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseover", onOver);
    document.addEventListener("mouseleave", onLeave);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <>
      <div
        ref={dotRef}
        className={`custom-cursor-dot ${hidden ? "hidden" : ""}`}
        aria-hidden="true"
      />
      <div
        ref={ringRef}
        className={`custom-cursor-ring ${grow ? "grow" : ""} ${hidden ? "hidden" : ""}`}
        aria-hidden="true"
      />
    </>
  );
};
